import React, { useState, useEffect } from "react";
import {
  WbSunny,
  Cloud,
  Opacity,
  Thunderstorm,
  AcUnit,
  Air,
  Visibility,
  WaterDrop,
} from "@mui/icons-material";
import "./WeatherApp.css";

const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const WeatherIcon = ({ weatherCode, size = "normal" }) => {
  const className = `weather-icon weather-icon-${size}`;

  if (!weatherCode) return <Cloud className={className} />;
  if (weatherCode >= 200 && weatherCode < 300)
    return <Thunderstorm className={`${className} weather-thunder`} />;
  if (weatherCode >= 300 && weatherCode < 600)
    return <Opacity className={`${className} weather-rain`} />;
  if (weatherCode >= 600 && weatherCode < 700)
    return <AcUnit className={`${className} weather-snow`} />;
  if (weatherCode === 800)
    return <WbSunny className={`${className} weather-sunny`} />;

  return <Cloud className={className} />;
};

export default function WeatherApp() {
  const [location, setLocation] = useState({
    city: "",
    region: "",
    country: "",
  });

  const [currentWeather, setCurrentWeather] = useState(null);
  const [weeklyForecast, setWeeklyForecast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiKey = "9425d3f5a8a01fb31de8543e8cce0c71";

  const fetchOpenWeatherData = async (lat, lon) => {
    const url = `https://api.openweathermap.org/data/3.0/onecall?lat=${lat}&lon=${lon}&appid=${apiKey}&units=metric`;
    const res = await fetch(url);

    if (!res.ok) throw new Error("Failed to fetch weather data");

    const data = await res.json();

    const current = {
      values: {
        temperature: data.current.temp,
        humidity: data.current.humidity,
        uvIndex: data.current.uvi,
        windSpeed: data.current.wind_speed,
        visibility: (data.current.visibility ?? 0) / 1000,
        cloudCover: data.current.clouds,
        precipitationProbability: (data.daily?.[0]?.pop ?? 0) * 100,
        weatherCode: data.current.weather?.[0]?.id,
        weatherDescription:
          data.current.weather?.[0]?.description || "Current conditions",
      },
    };

    const daily = (data.daily ?? []).slice(0, 7).map((day) => ({
      time: new Date(day.dt * 1000),
      values: {
        temperatureMax: day.temp.max,
        temperatureMin: day.temp.min,
        weatherCodeMax: day.weather?.[0]?.id,
        description: day.weather?.[0]?.description || "",
      },
    }));

    return { current, daily };
  };

  const fetchUserLocation = async () => {
    const ipRes = await fetch("https://api.ipify.org?format=json");
    const ipData = await ipRes.json();
    const ip = ipData.ip;

    const locRes = await fetch(`https://ipinfo.io/${ip}?token=113d6fbfe6ec3e`);
    const locData = await locRes.json();

    return {
      city: locData.city || "",
      region: locData.region || "",
      country: locData.country || "",
    };
  };

  useEffect(() => {
    const getWeatherData = async () => {
      setLoading(true);
      setError(null);

      try {
        const userLocation = await fetchUserLocation();
        setLocation(userLocation);

        const geoRes = await fetch(
          `https://api.openweathermap.org/geo/1.0/direct?q=${encodeURIComponent(
            userLocation.city,
          )}&limit=1&appid=${apiKey}`,
        );

        const geoResults = await geoRes.json();
        const geoData = geoResults?.[0];

        if (!geoData) throw new Error("City not found");

        const { current, daily } = await fetchOpenWeatherData(
          geoData.lat,
          geoData.lon,
        );

        setCurrentWeather(current);
        setWeeklyForecast(daily);
      } catch (err) {
        setError(err.message || "Unable to load weather data");
      } finally {
        setLoading(false);
      }
    };

    getWeatherData();
  }, []);

  if (loading) {
    return (
      <div className="weather-state">
        <div className="weather-loader"></div>
        <span>Loading weather information...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="weather-error">
        <Cloud />
        <strong>Weather unavailable</strong>
        <span>{error}</span>
      </div>
    );
  }

  const current = currentWeather?.values;

  const highlights = [
    { title: "UV Index", value: current?.uvIndex ?? "N/A", icon: <WbSunny /> },
    {
      title: "Wind Speed",
      value:
        current?.windSpeed !== undefined
          ? `${current.windSpeed.toFixed(1)} m/s`
          : "N/A",
      icon: <Air />,
    },
    {
      title: "Humidity",
      value: current?.humidity !== undefined ? `${current.humidity}%` : "N/A",
      icon: <WaterDrop />,
    },
    {
      title: "Visibility",
      value:
        current?.visibility !== undefined
          ? `${current.visibility.toFixed(1)} km`
          : "N/A",
      icon: <Visibility />,
    },
  ];

  return (
    <div className="weather-app">
      <div className="weather-dashboard">
        <section className="weather-current-card">
          <div className="weather-current-top">
            <div className="weather-current-label">
              <span className="weather-small-line"></span>
              CURRENT CONDITIONS
            </div>
          </div>

          <div className="weather-current-main">
            <WeatherIcon weatherCode={current?.weatherCode} size="large" />

            <div className="weather-temperature">
              {Math.round(current?.temperature)}°<span>C</span>
            </div>

            <div className="weather-description">
              {current?.weatherDescription}
            </div>
          </div>

          <div className="weather-current-details">
            <div className="weather-current-detail">
              <span>Cloud Cover</span>
              <strong>{current?.cloudCover ?? 0}%</strong>
            </div>

            <div className="weather-current-detail">
              <span>Rain Chance</span>
              <strong>
                {Math.round(current?.precipitationProbability ?? 0)}%
              </strong>
            </div>
          </div>

          <div className="weather-location">
            <span className="weather-location-dot"></span>
            <div>
              <strong>{location.city}</strong>
              <span>
                {location.region}
                {location.region && location.country ? ", " : ""}
                {location.country}
              </span>
            </div>
          </div>
        </section>

        <section className="weather-forecast-panel">
          <div className="weather-panel-heading">
            <div>
              <span className="weather-eyebrow">
                <span></span>
                07 DAY OUTLOOK
              </span>
              <h3>Weekly Forecast</h3>
            </div>
          </div>

          <div className="weather-forecast-grid">
            {weeklyForecast.map((day, index) => {
              const date = new Date(day.time);
              const dayName = weekDays[date.getDay()];
              const formattedDate = date.toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
              });

              return (
                <div
                  className={`weather-day-card ${index === 0 ? "active" : ""}`}
                  key={index}
                >
                  <span className="weather-day-name">
                    {index === 0 ? "TODAY" : dayName}
                  </span>

                  <span className="weather-day-date">{formattedDate}</span>

                  <WeatherIcon weatherCode={day.values.weatherCodeMax} />

                  <strong>{Math.round(day.values.temperatureMax)}°</strong>

                  <span className="weather-day-description">
                    {day.values.description}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="weather-highlights-heading">
            <div>
              <span className="weather-eyebrow">
                <span></span>
                FIELD CONDITIONS
              </span>
              <h3>Highlights</h3>
            </div>
          </div>

          <div className="weather-highlights-grid">
            {highlights.map((item) => (
              <div className="weather-highlight-card" key={item.title}>
                <div className="weather-highlight-icon">{item.icon}</div>
                <div className="weather-highlight-info">
                  <span>{item.title}</span>
                  <strong>{item.value}</strong>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
